import { MAX_SUPPORT_MESSAGE_LENGTH, SUPPORT_CATEGORY_LABELS } from '@groundly/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, CircleCheck, Clock, MessageSquareReply } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { isNotFound } from '../../../api/call.js';
import {
  useReplySupportTicket,
  useResolveSupportTicket,
  useSupportTicket,
} from '../../../api/support.js';
import { Button } from '../../../components/Button.js';
import { DangerAlert } from '../../../components/DangerAlert.js';
import { PageHeading } from '../../../components/PageHeading.js';
import { LoadingArea } from '../../../components/PlaceholderPage.js';
import { StatusBadge } from '../../../components/StatusBadge.js';
import { formatFullDateTime } from '../../../conversations/display.js';
import { clientStatus } from '../../../support/display.js';
import styles from '../../../support/support.module.css';

/**
 * `/support/:ticketId`: one support request, its thread with Groundly, and a
 * reply. Replying reopens a resolved request.
 */
export const Route = createFileRoute('/_app/support/$ticketId')({
  component: TicketPage,
});

const STATUS_ICONS = { attention: MessageSquareReply, neutral: Clock, success: CircleCheck };

function TicketPage() {
  const { ticketId } = Route.useParams();
  const ticket = useSupportTicket(ticketId);
  const reply = useReplySupportTicket(ticketId);
  const resolve = useResolveSupportTicket(ticketId);
  const [draft, setDraft] = useState('');

  const back = (
    <Link className={styles.back} to="/support">
      <ArrowLeft aria-hidden="true" width={16} height={16} strokeWidth={1.75} />
      Support
    </Link>
  );

  if (ticket.isPending) return <LoadingArea />;
  if (ticket.isError) {
    return (
      <>
        {back}
        <PageHeading>Support request</PageHeading>
        <DangerAlert
          title={
            isNotFound(ticket.error) ? 'This request doesn’t exist' : "This request didn't load"
          }
          action={
            isNotFound(ticket.error) ? undefined : (
              <Button onClick={() => void ticket.refetch()}>Try again</Button>
            )
          }
        >
          {isNotFound(ticket.error)
            ? 'It may belong to another account.'
            : 'Check your connection and try again.'}
        </DangerAlert>
      </>
    );
  }

  const data = ticket.data;
  const status = clientStatus(data.status);

  function send(event: FormEvent) {
    event.preventDefault();
    if (draft.trim() === '') return;
    reply.mutate(draft, { onSuccess: () => setDraft('') });
  }

  return (
    <>
      {back}
      <header className={styles.header}>
        <div>
          <PageHeading documentTitle={`#${data.number} ${data.subject}`}>
            {data.subject}
          </PageHeading>
          <p className={styles.caption}>
            #{data.number} · {SUPPORT_CATEGORY_LABELS[data.category]}
            {data.websiteName !== null ? ` · ${data.websiteName}` : ''}
          </p>
        </div>
        <StatusBadge tone={status.tone} icon={STATUS_ICONS[status.tone]}>
          {status.label}
        </StatusBadge>
      </header>

      <ol className={styles.thread}>
        {data.messages.map((message) => (
          <li
            key={message.id}
            className={message.authorKind === 'admin' ? styles.fromGroundly : styles.fromClient}
          >
            <p className={styles.author}>
              <strong>{message.authorName}</strong>{' '}
              <time dateTime={message.createdAt}>{formatFullDateTime(message.createdAt)}</time>
            </p>
            <p className={styles.body}>{message.content}</p>
          </li>
        ))}
      </ol>

      <form className={styles.form} onSubmit={send} noValidate>
        <label className={styles.label} htmlFor="support-reply">
          {data.status === 'resolved' ? 'Reply to reopen this request' : 'Reply'}
        </label>
        <textarea
          id="support-reply"
          className={styles.textarea}
          rows={4}
          maxLength={MAX_SUPPORT_MESSAGE_LENGTH}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />
        {reply.isError ? (
          <DangerAlert title="Your reply wasn't sent">
            Check your connection and try again.
          </DangerAlert>
        ) : null}
        <div className={styles.actions}>
          <Button variant="primary" type="submit" disabled={reply.isPending || draft.trim() === ''}>
            {reply.isPending ? 'Sending…' : 'Send reply'}
          </Button>
          {data.status !== 'resolved' ? (
            <Button variant="ghost" onClick={() => resolve.mutate()} disabled={resolve.isPending}>
              My issue is resolved
            </Button>
          ) : null}
        </div>
      </form>
    </>
  );
}
